import { useEffect, useRef } from 'react';

import { Button } from '@xing-com/button';
import type { ButtonSizes, ButtonVariants } from '@xing-com/button';
import { useActivityCenterTracking } from '@xing-com/crate-activity-center-tracking';
import {
  useActivityCenter,
  useActivityCenterButtonRendered,
} from '@xing-com/hub';
import type { ActivityCenterOpenSource } from '@xing-com/hub';
import { addParams } from '@xing-com/xing-tracking';

import * as Styled from './activity-center-login-button.styled';
import {
  NotificationBadgeWithCount,
  SuperEllipseWithTopBadge,
} from './super-ellipse-with-top-badge';

type ActivityCenterButtonProps = {
  variant?: ButtonVariants;
  title: string;
  href?: string;
  loginLink?: string;
  nwtNav?: string;
  openSource?: ActivityCenterOpenSource;
};

type ActivityCenterActionButtonProps = {
  count: number;
  navigateTo: string | undefined;
  onClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  size: ButtonSizes;
  title: string;
  variant: ButtonVariants;
};

const ActivityCenterActionButton: React.FC<ActivityCenterActionButtonProps> = ({
  count,
  navigateTo,
  onClick,
  size,
  title,
  variant,
}) => {
  return (
    <Styled.ButtonWithBadge $size={size}>
      <Button
        data-testid="activity-center-login-action"
        onClick={onClick}
        size={size}
        to={navigateTo}
        variant={variant}
      >
        {title}
      </Button>
      <NotificationBadgeWithCount>{count}</NotificationBadgeWithCount>
    </Styled.ButtonWithBadge>
  );
};

export const ActivityCenterLoginButton: React.FC<ActivityCenterButtonProps> = ({
  variant,
  title,
  href,
  loginLink,
  nwtNav = 'activitycenter',
  openSource = 'navigation',
}) => {
  useActivityCenterButtonRendered();

  const {
    total,
    isActivityCenterVisible,
    openActivityCenter,
    closeActivityCenter,
  } = useActivityCenter();
  const { trackInteraction } = useActivityCenterTracking();

  const justClosedRef = useRef<boolean>(false);
  const trackingURL =
    loginLink &&
    addParams(loginLink, {
      nwt_nav: nwtNav,
      signup_channel: 'minireg_activitycenter',
    });
  const navigateTo = total <= 0 && trackingURL ? trackingURL : href;

  useEffect(() => {
    if (isActivityCenterVisible) return;

    justClosedRef.current = true;

    const timerId = setTimeout(() => {
      justClosedRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timerId);
      justClosedRef.current = false;
    };
  }, [isActivityCenterVisible]);

  const onClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ): void => {
    if (total > 0) {
      event.preventDefault();

      if (!justClosedRef.current) {
        if (isActivityCenterVisible) {
          closeActivityCenter();
        } else {
          openActivityCenter(openSource);
        }
      }

      trackInteraction({
        event: 'clicked',
        element: 'activitycenter',
        elementDetail: 'activitycenter_open',
      });
    }
  };

  if (variant) {
    return (
      <Styled.ActionButtons id="activity-center-button">
        <ActivityCenterActionButton
          count={total}
          navigateTo={navigateTo}
          onClick={onClick}
          size="small"
          title={title}
          variant={variant}
        />
        <ActivityCenterActionButton
          count={total}
          navigateTo={navigateTo}
          onClick={onClick}
          size="medium"
          title={title}
          variant={variant}
        />
      </Styled.ActionButtons>
    );
  }

  return (
    <Styled.Link
      id="activity-center-button"
      data-testid="frame-vnav-activity-center-login-button"
      to={navigateTo}
      aria-label={title}
      onClick={onClick}
    >
      <SuperEllipseWithTopBadge count={total} />
      {title}
    </Styled.Link>
  );
};
